import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CATEGORIES, SECTIONS, ITEMS, LANGS, label } from '../../data/menuData';
import './menu.css';

export default function MenuPopup({ open, onClose }) {
  const dialogRef = useRef(null);
  const listRef = useRef(null);
  const [lang, setLang] = useState('es');
  const [cat, setCat] = useState('med');
  const subKeys = useMemo(() => SECTIONS[cat].map((section) => section.id), [cat]);
  const [sub, setSub] = useState(subKeys[0]);

  useEffect(() => {
    const first = (SECTIONS[cat] && SECTIONS[cat][0]?.id) || '';
    setSub(first);
  }, [cat]);

  useEffect(() => {
    function onKey(event) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    if (open) {
      document.addEventListener('keydown', onKey);
    }

    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (open && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (open && listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [open, cat, sub]);

  if (!open) {
    return null;
  }

  const sectionList = ITEMS[cat][sub] || [];
  const activeCategory = CATEGORIES.find((category) => category.id === cat);
  const activeSection = SECTIONS[cat].find((section) => section.id === sub);
  const visibleItemCount = sectionList.filter(
    (item) => item[`name_${lang}`] || item.name || item.name_es
  ).length;

  return (
    <div className="vt-modal" role="dialog" aria-modal="true">
      <div className="vt-modal-backdrop" onClick={onClose} />

      <div
        className="vt-modal-card"
        ref={dialogRef}
        tabIndex={-1}
        aria-labelledby="vt-modal-title"
      >
        <div className="vt-topbar">
          <div className="vt-catbar">
            {CATEGORIES.map((category) => (
              <button
                key={category.id}
                type="button"
                className={`vt-cat ${cat === category.id ? 'active' : ''}`}
                onClick={() => setCat(category.id)}
              >
                {label(category, lang)}
              </button>
            ))}
          </div>

          <div className="vt-toolbar-actions">
            <select
              className="vt-lang"
              value={lang}
              onChange={(event) => setLang(event.target.value)}
              aria-label="Menu language"
            >
              {LANGS.map((code) => (
                <option key={code} value={code}>
                  {code.toUpperCase()}
                </option>
              ))}
            </select>

            <button type="button" className="vt-close" onClick={onClose} aria-label="Close menu">
              x
            </button>
          </div>
        </div>

        <div className="vt-subnav" role="tablist">
          {SECTIONS[cat].map((section) => (
            <button
              key={section.id}
              type="button"
              role="tab"
              aria-selected={sub === section.id}
              className={`vt-subtab ${sub === section.id ? 'active' : ''}`}
              onClick={() => setSub(section.id)}
            >
              {label(section, lang)}
            </button>
          ))}
        </div>

        <div className="vt-section-summary">
          <div>
            <p className="vt-section-eyebrow">{label(activeCategory, lang)}</p>
            <h3 id="vt-modal-title">{label(activeSection, lang)}</h3>
          </div>

          <p className="vt-section-meta">{visibleItemCount} curated choices</p>
        </div>

        <div className="vt-list" ref={listRef} role="region" aria-label={`${cat} - ${sub}`}>
          {sectionList.map((item, index) => (
            <div key={`${sub}-${index}`} className="vt-item">
              {item.header && <div className="vt-sectionheader">{label(item, lang)}</div>}
              {item.note && <div className="vt-note">{label(item, lang)}</div>}

              {(item[`name_${lang}`] || item.name || item.name_es) && (
                <>
                  <div className="vt-item-row">
                    <div className="vt-item-name">
                      {item[`name_${lang}`] || item.name || item.name_es}
                    </div>
                    <span className="vt-item-dots" aria-hidden="true" />
                    <div className="vt-item-price">{item.price || ''}</div>
                  </div>

                  {(item[`desc_${lang}`] || item.desc || item.desc_es) && (
                    <div className="vt-item-desc">
                      {item[`desc_${lang}`] || item.desc || item.desc_es}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
